import type {PropsWithChildren} from 'react';
import {useCallback, useMemo, useRef} from 'react';
import {useListState} from '@react-stately/list';
import {useGridList} from '@react-aria/gridlist';
import {Item as ListItem} from '@react-stately/collections';
import classNames from 'classnames';
import type {AriaGridListProps} from '@react-aria/gridlist';

import {useCopyToClipboardFunc} from '../../../hooks/copy-to-clipboard';

import {TypographySize, useTypography} from '../../../hooks/typography';

import type {ProfileListItem, ProfileListProps} from './types';
import {Item} from './components/item';
import {Icon} from './components/item/icon';
import {Title} from './components/item/title';
import {Content} from './components/item/content';

import styles from './styles.module.css';

function List(props: AriaGridListProps<ProfileListItem> & { rawItems: ProfileListItem[] }) {
    const state = useListState(props);
    const ref = useRef<HTMLUListElement>(null);

    const {gridProps} = useGridList(props, state, ref);

    return (
        <ul className={styles.list} ref={ref} {...gridProps}>
            {[...state.collection].map((item) => (
                <Item
                    key={item.key}
                    item={item}
                    rawItem={props.rawItems.find((rawItem) => rawItem.id === item.key) as ProfileListItem}
                    state={state}
                />
            ))}
        </ul>
    );
}

function ListTitle(props: PropsWithChildren<{ level?: 1 | 2 | 3 | 4 | 5 | 6 }>) {
    const {level = 6} = props;

    const typographyCls = useTypography({
        size: TypographySize.BodyXSTrue,
        noDefaultColor: true,
    });

    const Heading = `h${level}` as const;

    return <Heading className={classNames(typographyCls, styles.title)}>{props.children}</Heading>;
}

export function ProfileList(props: ProfileListProps) {
    const {items, title, titleLevel = 6} = props;
    const hasTitle = Boolean(title);
    const hasItems = Boolean(items);
    const copyToClipboard = useCopyToClipboardFunc();

    const disabledKeys = useMemo(() => {
        return items.
            filter((item) => {
                if (typeof item.onPress === 'function') {
                    return false;
                }

                if (item.clipboardData) {
                    return false;
                }

                return true;
            }).
            map(({id}) => id);
    }, [items]);

    const handleAction = useCallback(
        (id) => {
            const listItem = items.find((item) => item.id === id);

            if (listItem?.onPress && typeof listItem?.onPress === 'function') {
                return listItem.onPress();
            }

            if (listItem?.clipboardData) {
                return copyToClipboard(listItem.clipboardData);
            }
        },
        [items, copyToClipboard],
    );

    if (!hasItems && !hasTitle) {
        return null;
    }

    return (
        <section>
            {hasTitle && <ListTitle level={titleLevel}>{props.title}</ListTitle>}
            <List
                aria-label={title}
                disabledKeys={disabledKeys}
                selectionMode='none'
                onAction={handleAction}
                rawItems={props.items}
            >
                {props.items.map((item) => (
                    <ListItem key={item.id} textValue={item.id}>
                        <Icon>{item.icon}</Icon>
                        <Title>{item.title}</Title>
                        <Content>{item.content}</Content>
                    </ListItem>
                ))}
            </List>
        </section>
    );
}
